var TokenType = /* @__PURE__ */ ((TokenType2) => {
  TokenType2["IDENTIFIER"] = "identifier";
  TokenType2["STRING"] = "string";
  TokenType2["NUMBER"] = "number";
  TokenType2["TEMPLATE_LITERAL"] = "template_literal";
  TokenType2["OPERATOR"] = "operator";
  TokenType2["WHITESPACE"] = "whitespace";
  TokenType2["MEMBER_EXPRESSION"] = "member_expression";
  TokenType2["PROPERTY_ACCESS"] = "property_access";
  TokenType2["BRACKET_ACCESS"] = "bracket_access";
  TokenType2["FUNCTION_CALL"] = "function_call";
  TokenType2["PARENTHESIZED"] = "parenthesized";
  TokenType2["OBJECT_LITERAL"] = "object_literal";
  TokenType2["ARRAY_LITERAL"] = "array_literal";
  TokenType2["TERNARY"] = "ternary";
  TokenType2["BINARY"] = "binary";
  TokenType2["ROOT"] = "root";
  TokenType2["EOF"] = "eof";
  return TokenType2;
})(TokenType || {});
class Token {
  constructor(type, value, start, end) {
    this.parent = null;
    this.children = [];
    this.isOptional = false;
    // 연산자 주변 공백 보존용
    this.leftSpace = "";
    this.rightSpace = "";
    this.type = type;
    this.value = value;
    this.start = start;
    this.end = end;
  }
  // --- Child management ---
  addChild(child) {
    child.parent = this;
    this.children.push(child);
    return this;
  }
  getChildren() {
    return this.children;
  }
  hasChildren() {
    return this.children.length > 0;
  }
  getParent() {
    return this.parent;
  }
  // --- Type checks ---
  isIdentifier() {
    return this.type === "identifier" /* IDENTIFIER */;
  }
  isString() {
    return this.type === "string" /* STRING */;
  }
  isNumber() {
    return this.type === "number" /* NUMBER */;
  }
  isTemplateLiteral() {
    return this.type === "template_literal" /* TEMPLATE_LITERAL */;
  }
  isOperator() {
    return this.type === "operator" /* OPERATOR */;
  }
  isWhitespace() {
    return this.type === "whitespace" /* WHITESPACE */;
  }
  isMemberExpression() {
    return this.type === "member_expression" /* MEMBER_EXPRESSION */;
  }
  isPropertyAccess() {
    return this.type === "property_access" /* PROPERTY_ACCESS */;
  }
  isBracketAccess() {
    return this.type === "bracket_access" /* BRACKET_ACCESS */;
  }
  isFunctionCall() {
    return this.type === "function_call" /* FUNCTION_CALL */;
  }
  isParenthesized() {
    return this.type === "parenthesized" /* PARENTHESIZED */;
  }
  isObjectLiteral() {
    return this.type === "object_literal" /* OBJECT_LITERAL */;
  }
  isArrayLiteral() {
    return this.type === "array_literal" /* ARRAY_LITERAL */;
  }
  isTernary() {
    return this.type === "ternary" /* TERNARY */;
  }
  isBinary() {
    return this.type === "binary" /* BINARY */;
  }
  isRoot() {
    return this.type === "root" /* ROOT */;
  }
  isEOF() {
    return this.type === "eof" /* EOF */;
  }
  // --- Keyword check for identifiers ---
  isKeyword() {
    if (this.type !== "identifier" /* IDENTIFIER */) return false;
    return ["true", "false", "null", "undefined", "this", "window", "document"].includes(this.value);
  }
  // --- Operator checks ---
  isLogicalOperator() {
    return this.type === "operator" /* OPERATOR */ && (this.value === "||" || this.value === "&&");
  }
  isNullishOperator() {
    return this.type === "operator" /* OPERATOR */ && this.value === "??";
  }
  isComparisonOperator() {
    return this.type === "operator" /* OPERATOR */ && ["===", "!==", "==", "!=", "<", ">", "<=", ">="].includes(this.value);
  }
  isArithmeticOperator() {
    return this.type === "operator" /* OPERATOR */ && ["+", "-", "*", "/", "%"].includes(this.value);
  }
  // --- Stringify ---
  stringify() {
    if (this.children.length === 0) {
      return this.value;
    }
    return this.children.map((c) => c.stringify()).join("");
  }
  // --- Optional chain conversion ---
  toOptionalChain(isFirst = false) {
    switch (this.type) {
      case "property_access" /* PROPERTY_ACCESS */:
        return `?.${this.children.map((c) => c.toOptionalChain()).join("")}`;
      case "bracket_access" /* BRACKET_ACCESS */:
        if (isFirst) {
          return `[${this.children.map((c) => c.toOptionalChain()).join("")}]`;
        }
        return `?.[${this.children.map((c) => c.toOptionalChain()).join("")}]`;
      case "function_call" /* FUNCTION_CALL */:
        return `?.(${this.children.map((c) => c.toOptionalChain()).join("")})`;
      case "member_expression" /* MEMBER_EXPRESSION */:
        if (this.children.length === 0) return this.value;
        let result = "";
        for (let i = 0; i < this.children.length; i++) {
          const child = this.children[i];
          result += child.toOptionalChain(i === 0);
        }
        return result;
      case "parenthesized" /* PARENTHESIZED */:
        if (this.isOptional) {
          return `?.(${this.children.map((c) => c.toOptionalChain()).join("")})`;
        }
        if (this.parent?.type === "member_expression" /* MEMBER_EXPRESSION */ && !isFirst) {
          return `?.(${this.children.map((c) => c.toOptionalChain()).join("")})`;
        }
        return `(${this.children.map((c) => c.toOptionalChain()).join("")})`;
      case "ternary" /* TERNARY */:
        if (this.children.length >= 3) {
          return `${this.children[0].toOptionalChain()} ? ${this.children[1].toOptionalChain()} : ${this.children[2].toOptionalChain()}`;
        }
        return this.stringify();
      case "binary" /* BINARY */:
        if (this.children.length >= 2) {
          const op = this.value;
          const leftSp = this.leftSpace;
          const rightSp = this.rightSpace;
          return `${this.children[0].toOptionalChain()}${leftSp}${op}${rightSp}${this.children[1].toOptionalChain()}`;
        }
        return this.stringify();
      case "root" /* ROOT */:
        return this.children.map((c) => c.toOptionalChain()).join("");
      case "object_literal" /* OBJECT_LITERAL */:
      case "array_literal" /* ARRAY_LITERAL */:
      case "string" /* STRING */:
      case "number" /* NUMBER */:
      case "template_literal" /* TEMPLATE_LITERAL */:
        return this.value;
      case "operator" /* OPERATOR */:
        if (this.children.length > 0) {
          return `${this.value}${this.children.map((c) => c.toOptionalChain()).join("")}`;
        }
        return this.value;
      default:
        return this.value;
    }
  }
  // --- Tree traversal ---
  walk(callback, depth = 0) {
    callback(this, depth);
    for (const child of this.children) {
      child.walk(callback, depth + 1);
    }
  }
  find(predicate) {
    if (predicate(this)) return this;
    for (const child of this.children) {
      const found = child.find(predicate);
      if (found) return found;
    }
    return null;
  }
  findAll(predicate) {
    const results = [];
    this.walk((token) => {
      if (predicate(token)) results.push(token);
    });
    return results;
  }
}
class ObjectPathParser {
  constructor(input = "") {
    this.pos = 0;
    this.input = input;
    this.root = new Token("root" /* ROOT */, "", 0, input.length);
    if (input) {
      this.parse();
    }
  }
  // --- Getters ---
  getInput() {
    return this.input;
  }
  getRoot() {
    return this.root;
  }
  getTokens() {
    return this.root.children;
  }
  // --- Flat token list (for backward compatibility) ---
  getFlatTokens() {
    const tokens = [];
    this.root.walk((token) => {
      if (token.type !== "root" /* ROOT */) {
        tokens.push(token);
      }
    });
    return tokens;
  }
  // --- Helper methods ---
  isFunctionScript() {
    return this.input.trim().startsWith("function");
  }
  isArrowFunctionScript() {
    return /^\s*\([^)]*\)\s*=>/.test(this.input);
  }
  isObjectLiteralInput() {
    const trimmed = this.input.trim();
    return trimmed.startsWith("{") && trimmed.endsWith("}");
  }
  // --- Tokenizer helpers ---
  peek(offset = 0) {
    return this.input[this.pos + offset] || "";
  }
  advance() {
    return this.input[this.pos++] || "";
  }
  isEOF() {
    return this.pos >= this.input.length;
  }
  skipWhitespace() {
    while (!this.isEOF() && /\s/.test(this.peek())) {
      this.advance();
    }
  }
  // --- Main parse method ---
  parse() {
    this.pos = 0;
    this.root = new Token("root" /* ROOT */, "", 0, this.input.length);
    while (!this.isEOF()) {
      this.skipWhitespace();
      if (this.isEOF()) break;
      const token = this.parseExpression();
      if (token) {
        this.root.addChild(token);
      }
    }
  }
  parseExpression() {
    this.skipWhitespace();
    if (this.isEOF()) return null;
    const ternary = this.tryParseTernary();
    if (ternary) return ternary;
    const binary = this.tryParseBinary();
    if (binary) return binary;
    return this.parsePrimaryExpression();
  }
  parsePrimaryExpression() {
    this.skipWhitespace();
    if (this.isEOF()) return null;
    const start = this.pos;
    const char = this.peek();
    if (char === "'" || char === '"') {
      return this.parseString(char);
    }
    if (char === "`") {
      return this.parseTemplateLiteral();
    }
    if (char === "{") {
      return this.parseObjectLiteral();
    }
    if (char === "[") {
      return this.parseArrayOrBracket();
    }
    if (char === "(") {
      return this.parseParenthesized();
    }
    if (/\d/.test(char)) {
      return this.parseNumber();
    }
    if (/[a-zA-Z_$]/.test(char)) {
      return this.parseMemberExpression();
    }
    if (char === "!") {
      return this.parseUnaryNot();
    }
    if (this.isOperatorChar(char)) {
      return this.parseOperator();
    }
    this.advance();
    return null;
  }
  // Parse unary NOT operator (!expr)
  parseUnaryNot() {
    const start = this.pos;
    this.advance();
    this.skipWhitespace();
    const expr = this.parsePrimaryExpression();
    if (expr) {
      const unary = new Token("operator" /* OPERATOR */, "!", start, this.pos);
      unary.addChild(expr);
      return unary;
    }
    return new Token("operator" /* OPERATOR */, "!", start, this.pos);
  }
  parseMemberExpression() {
    const start = this.pos;
    const memberExpr = new Token("member_expression" /* MEMBER_EXPRESSION */, "", start, start);
    const identifier = this.parseIdentifier();
    memberExpr.addChild(identifier);
    while (!this.isEOF()) {
      const char = this.peek();
      if (char === "?" && this.peek(1) === ".") {
        this.advance();
        this.advance();
        if (this.peek() === "[") {
          const bracket = this.parseBracketAccess(true);
          memberExpr.addChild(bracket);
        } else if (this.peek() === "(") {
          const funcCall = this.parseFunctionCall(true);
          memberExpr.addChild(funcCall);
        } else {
          const prop = this.parsePropertyAccess(true);
          memberExpr.addChild(prop);
        }
      } else if (char === ".") {
        this.advance();
        if (this.peek() === "(") {
          const paren = this.parseParenthesizedAccess(false);
          memberExpr.addChild(paren);
        } else {
          const prop = this.parsePropertyAccess(false);
          memberExpr.addChild(prop);
        }
      } else if (char === "[") {
        const bracket = this.parseBracketAccess(false);
        memberExpr.addChild(bracket);
      } else if (char === "(") {
        const funcCall = this.parseFunctionCall(false);
        memberExpr.addChild(funcCall);
      } else {
        break;
      }
    }
    memberExpr.end = this.pos;
    memberExpr.value = this.input.substring(start, this.pos);
    return memberExpr;
  }
  // 괄호로 감싸진 속성 접근 (e.g., a.(b).c)
  parseParenthesizedAccess(isOptional) {
    const start = this.pos;
    this.advance();
    const paren = new Token("parenthesized" /* PARENTHESIZED */, "", start, start);
    paren.isOptional = isOptional;
    let depth = 1;
    let content = "";
    while (!this.isEOF() && depth > 0) {
      const char = this.peek();
      if (char === "(") depth++;
      else if (char === ")") {
        depth--;
        if (depth === 0) break;
      }
      content += this.advance();
    }
    if (content.trim()) {
      const innerParser = new ObjectPathParser(content);
      for (const child of innerParser.getTokens()) {
        paren.addChild(child);
      }
    }
    if (this.peek() === ")") this.advance();
    paren.end = this.pos;
    paren.value = this.input.substring(start, this.pos);
    return paren;
  }
  parseIdentifier() {
    const start = this.pos;
    let value = "";
    while (!this.isEOF() && /[a-zA-Z0-9_$]/.test(this.peek())) {
      value += this.advance();
    }
    return new Token("identifier" /* IDENTIFIER */, value, start, this.pos);
  }
  parsePropertyAccess(isOptional) {
    const start = this.pos;
    const prop = new Token("property_access" /* PROPERTY_ACCESS */, "", start, start);
    prop.isOptional = isOptional;
    const identifier = this.parseIdentifier();
    prop.addChild(identifier);
    prop.end = this.pos;
    prop.value = this.input.substring(start, this.pos);
    return prop;
  }
  parseBracketAccess(isOptional) {
    const start = this.pos;
    this.advance();
    const bracket = new Token("bracket_access" /* BRACKET_ACCESS */, "", start, start);
    bracket.isOptional = isOptional;
    const contentStart = this.pos;
    let depth = 1;
    let content = "";
    while (!this.isEOF() && depth > 0) {
      const char = this.peek();
      if (char === "[") depth++;
      else if (char === "]") {
        depth--;
        if (depth === 0) break;
      }
      content += this.advance();
    }
    if (content.trim()) {
      const innerParser = new ObjectPathParser(content);
      for (const child of innerParser.getTokens()) {
        bracket.addChild(child);
      }
    }
    if (this.peek() === "]") this.advance();
    bracket.end = this.pos;
    bracket.value = this.input.substring(start, this.pos);
    return bracket;
  }
  parseFunctionCall(isOptional) {
    const start = this.pos;
    this.advance();
    const funcCall = new Token("function_call" /* FUNCTION_CALL */, "", start, start);
    funcCall.isOptional = isOptional;
    let depth = 1;
    let content = "";
    while (!this.isEOF() && depth > 0) {
      const char = this.peek();
      if (char === "(") depth++;
      else if (char === ")") {
        depth--;
        if (depth === 0) break;
      }
      content += this.advance();
    }
    if (content.trim()) {
      const innerParser = new ObjectPathParser(content);
      for (const child of innerParser.getTokens()) {
        funcCall.addChild(child);
      }
    }
    if (this.peek() === ")") this.advance();
    funcCall.end = this.pos;
    funcCall.value = this.input.substring(start, this.pos);
    return funcCall;
  }
  parseParenthesized() {
    const start = this.pos;
    this.advance();
    const paren = new Token("parenthesized" /* PARENTHESIZED */, "", start, start);
    let depth = 1;
    let content = "";
    while (!this.isEOF() && depth > 0) {
      const char = this.peek();
      if (char === "(") depth++;
      else if (char === ")") {
        depth--;
        if (depth === 0) break;
      }
      content += this.advance();
    }
    if (content.trim()) {
      const innerParser = new ObjectPathParser(content);
      for (const child of innerParser.getTokens()) {
        paren.addChild(child);
      }
    }
    if (this.peek() === ")") this.advance();
    paren.end = this.pos;
    paren.value = this.input.substring(start, this.pos);
    if (this.peek() === "." || this.peek() === "[" || this.peek() === "?" && this.peek(1) === ".") {
      const memberExpr = new Token("member_expression" /* MEMBER_EXPRESSION */, "", start, start);
      memberExpr.addChild(paren);
      while (!this.isEOF()) {
        const char = this.peek();
        if (char === "?" && this.peek(1) === ".") {
          this.advance();
          this.advance();
          if (this.peek() === "[") {
            const bracket = this.parseBracketAccess(true);
            memberExpr.addChild(bracket);
          } else if (this.peek() === "(") {
            const funcCall = this.parseFunctionCall(true);
            memberExpr.addChild(funcCall);
          } else {
            const prop = this.parsePropertyAccess(true);
            memberExpr.addChild(prop);
          }
        } else if (char === ".") {
          this.advance();
          if (this.peek() === "(") {
            const parenAccess = this.parseParenthesizedAccess(false);
            memberExpr.addChild(parenAccess);
          } else {
            const prop = this.parsePropertyAccess(false);
            memberExpr.addChild(prop);
          }
        } else if (char === "[") {
          const bracket = this.parseBracketAccess(false);
          memberExpr.addChild(bracket);
        } else {
          break;
        }
      }
      memberExpr.end = this.pos;
      memberExpr.value = this.input.substring(start, this.pos);
      return memberExpr;
    }
    return paren;
  }
  parseString(quote) {
    const start = this.pos;
    let value = this.advance();
    while (!this.isEOF() && this.peek() !== quote) {
      if (this.peek() === "\\") {
        value += this.advance();
        if (!this.isEOF()) value += this.advance();
      } else {
        value += this.advance();
      }
    }
    if (!this.isEOF()) value += this.advance();
    return new Token("string" /* STRING */, value, start, this.pos);
  }
  parseTemplateLiteral() {
    const start = this.pos;
    let value = this.advance();
    let depth = 0;
    while (!this.isEOF()) {
      const char = this.peek();
      if (char === "`" && depth === 0) {
        value += this.advance();
        break;
      }
      if (char === "$" && this.peek(1) === "{") {
        value += this.advance();
        value += this.advance();
        depth++;
        let exprContent = "";
        let exprDepth = 1;
        while (!this.isEOF() && exprDepth > 0) {
          const c = this.peek();
          if (c === "{") exprDepth++;
          else if (c === "}") {
            exprDepth--;
            if (exprDepth === 0) break;
          }
          exprContent += this.advance();
        }
        if (exprContent.trim()) {
          const innerParser = new ObjectPathParser(exprContent);
          value += innerParser.toOptionalChainPath();
        }
        if (this.peek() === "}") {
          value += this.advance();
          depth--;
        }
        continue;
      }
      if (char === "\\") {
        value += this.advance();
        if (!this.isEOF()) value += this.advance();
      } else {
        value += this.advance();
      }
    }
    return new Token("template_literal" /* TEMPLATE_LITERAL */, value, start, this.pos);
  }
  parseNumber() {
    const start = this.pos;
    let value = "";
    while (!this.isEOF() && /[\d.]/.test(this.peek())) {
      value += this.advance();
    }
    return new Token("number" /* NUMBER */, value, start, this.pos);
  }
  parseObjectLiteral() {
    const start = this.pos;
    this.advance();
    let depth = 1;
    let content = "{";
    while (!this.isEOF() && depth > 0) {
      const char = this.peek();
      if (char === "{") depth++;
      else if (char === "}") depth--;
      content += this.advance();
    }
    return new Token("object_literal" /* OBJECT_LITERAL */, content, start, this.pos);
  }
  parseArrayOrBracket() {
    const start = this.pos;
    this.advance();
    let depth = 1;
    let content = "";
    while (!this.isEOF() && depth > 0) {
      const char = this.peek();
      if (char === "[") depth++;
      else if (char === "]") {
        depth--;
        if (depth === 0) break;
      }
      content += this.advance();
    }
    if (this.peek() === "]") this.advance();
    if (this.peek() === "." || this.peek() === "[") {
      const bracket = new Token("bracket_access" /* BRACKET_ACCESS */, `[${content}]`, start, this.pos);
      if (content.trim()) {
        const innerParser = new ObjectPathParser(content);
        for (const child of innerParser.getTokens()) {
          bracket.addChild(child);
        }
      }
      const memberExpr = new Token("member_expression" /* MEMBER_EXPRESSION */, "", start, start);
      memberExpr.addChild(bracket);
      while (!this.isEOF()) {
        const char = this.peek();
        if (char === ".") {
          this.advance();
          memberExpr.addChild(this.parsePropertyAccess(false));
        } else if (char === "[") {
          memberExpr.addChild(this.parseBracketAccess(false));
        } else {
          break;
        }
      }
      memberExpr.end = this.pos;
      memberExpr.value = this.input.substring(start, this.pos);
      return memberExpr;
    }
    return new Token("array_literal" /* ARRAY_LITERAL */, `[${content}]`, start, this.pos);
  }
  isOperatorChar(char) {
    return ["+", "-", "*", "/", "%", "=", "!", "<", ">", "&", "|", "?"].includes(char);
  }
  parseOperator() {
    const start = this.pos;
    let value = this.advance();
    const next = this.peek();
    if ((value === "=" || value === "!" || value === "<" || value === ">") && next === "=") {
      value += this.advance();
      if (this.peek() === "=") value += this.advance();
    } else if (value === "&" && next === "&" || value === "|" && next === "|") {
      value += this.advance();
    } else if (value === "?" && next === "?") {
      value += this.advance();
    }
    return new Token("operator" /* OPERATOR */, value, start, this.pos);
  }
  tryParseTernary() {
    const savedPos = this.pos;
    const remaining = this.input.substring(this.pos);
    let depth = 0;
    let inString = false;
    let stringChar = "";
    let questionIdx = -1;
    let colonIdx = -1;
    for (let i = 0; i < remaining.length; i++) {
      const char = remaining[i];
      const prevChar = i > 0 ? remaining[i - 1] : "";
      if ((char === '"' || char === "'" || char === "`") && prevChar !== "\\") {
        if (!inString) {
          inString = true;
          stringChar = char;
        } else if (char === stringChar) {
          inString = false;
          stringChar = "";
        }
      }
      if (inString) continue;
      if (char === "(" || char === "[" || char === "{") depth++;
      else if (char === ")" || char === "]" || char === "}") depth--;
      if (depth === 0 && char === "?") {
        const nextChar = remaining[i + 1] || "";
        if (nextChar === "." || nextChar === "?") continue;
        questionIdx = i;
      }
      if (depth === 0 && char === ":" && questionIdx >= 0 && colonIdx < 0) {
        colonIdx = i;
        break;
      }
    }
    if (questionIdx < 0 || colonIdx < 0) return null;
    const condStr = remaining.substring(0, questionIdx).trim();
    const trueStr = remaining.substring(questionIdx + 1, colonIdx).trim();
    const falseStr = remaining.substring(colonIdx + 1).trim();
    if (!condStr || !trueStr || !falseStr) return null;
    const ternary = new Token("ternary" /* TERNARY */, remaining, savedPos, savedPos + remaining.length);
    const condParser = new ObjectPathParser(condStr);
    const trueParser = new ObjectPathParser(trueStr);
    const falseParser = new ObjectPathParser(falseStr);
    for (const child of condParser.getTokens()) ternary.addChild(child);
    for (const child of trueParser.getTokens()) ternary.addChild(child);
    for (const child of falseParser.getTokens()) ternary.addChild(child);
    this.pos = this.input.length;
    return ternary;
  }
  tryParseBinary() {
    const remaining = this.input.substring(this.pos);
    const operatorGroups = [
      ["||"],
      // Logical OR (lowest precedence)
      ["&&"],
      // Logical AND
      ["??"],
      // Nullish coalescing
      ["<", ">", "<=", ">="],
      // Comparison
      ["===", "!==", "==", "!="],
      // Equality
      ["+", "-"],
      // Addition/Subtraction
      ["*", "/", "%"]
      // Multiplication/Division (highest precedence)
    ];
    for (const operators of operatorGroups) {
      for (const op of operators) {
        let depth = 0;
        let inString = false;
        let stringChar = "";
        let lastMatchIndex = -1;
        for (let i = 0; i < remaining.length - op.length + 1; i++) {
          const char = remaining[i];
          if ((char === '"' || char === "'" || char === "`") && (i === 0 || remaining[i - 1] !== "\\")) {
            if (!inString) {
              inString = true;
              stringChar = char;
            } else if (char === stringChar) {
              inString = false;
            }
          }
          if (inString) continue;
          if (char === "(" || char === "[" || char === "{") depth++;
          else if (char === ")" || char === "]" || char === "}") depth--;
          if (depth === 0 && remaining.substring(i, i + op.length) === op) {
            if (op === "?" && remaining[i + 1] === ".") continue;
            if (op === "-") {
              let lastNonSpace = "";
              for (let j = i - 1; j >= 0; j--) {
                if (remaining[j].trim()) {
                  lastNonSpace = remaining[j];
                  break;
                }
              }
              if (!lastNonSpace || /[+\-*\/%=<>!&|,(\[]/.test(lastNonSpace)) {
                continue;
              }
            }
            if (["<", ">", "=", "!"].includes(op)) {
              const nextChar = remaining[i + op.length] || "";
              if (nextChar === "=") continue;
            }
            lastMatchIndex = i;
          }
        }
        if (lastMatchIndex >= 0) {
          const i = lastMatchIndex;
          const leftRaw = remaining.substring(0, i);
          const rightRaw = remaining.substring(i + op.length);
          const leftStr = leftRaw.trim();
          const rightStr = rightRaw.trim();
          if (leftStr && rightStr) {
            const binary = new Token("binary" /* BINARY */, op, this.pos, this.pos + remaining.length);
            const leftTrailingSpace = leftRaw.substring(leftStr.length);
            const rightLeadingSpace = rightRaw.substring(0, rightRaw.length - rightRaw.trimStart().length);
            binary.leftSpace = leftTrailingSpace;
            binary.rightSpace = rightLeadingSpace;
            const leftParser = new ObjectPathParser(leftStr);
            const rightParser = new ObjectPathParser(rightStr);
            const leftContainer = new Token("root" /* ROOT */, leftStr, 0, leftStr.length);
            for (const child of leftParser.getTokens()) leftContainer.addChild(child);
            binary.addChild(leftContainer);
            const rightContainer = new Token("root" /* ROOT */, rightStr, 0, rightStr.length);
            for (const child of rightParser.getTokens()) rightContainer.addChild(child);
            binary.addChild(rightContainer);
            this.pos = this.input.length;
            return binary;
          }
        }
      }
    }
    return null;
  }
  // --- Main conversion method ---
  toOptionalChainPath() {
    if (!this.input) return "";
    if (this.isObjectLiteralInput()) return this.input;
    if (this.isFunctionScript() || this.isArrowFunctionScript()) return this.input;
    return this.root.toOptionalChain();
  }
  removeOptionalChainOperator() {
    if (!this.input) return "";
    return this.input.replace(/\?\./g, ".").replace(/\.\[/g, "[");
  }
  stringify() {
    return this.root.stringify();
  }
  // --- Static factory methods ---
  static parse(input) {
    return new ObjectPathParser(input);
  }
  static isFunctionScript(script) {
    return script.trim().startsWith("function");
  }
  static isArrowFunctionScript(script) {
    return /^\s*\([^)]*\)\s*=>/.test(script);
  }
  static isObjectLiteral(script) {
    const trimmed = script.trim();
    return trimmed.startsWith("{") && trimmed.endsWith("}");
  }
}
export {
  ObjectPathParser,
  Token,
  TokenType
};
//# sourceMappingURL=ObjectPathParser.js.map
